import JSZip from 'jszip';
import type { NostrEvent } from '@nostrify/nostrify';
import { getTagValue, parsePageTags, type PageEntry, spaceSlugFromDTag, STELE_PAGE_KIND, STELE_SPACE_KIND } from './stele';

interface ExportOptions {
  spaceEvent: NostrEvent;
  pageEvents: NostrEvent[];
}

export async function exportSpaceAsZip({ spaceEvent, pageEvents }: ExportOptions): Promise<void> {
  const zip = new JSZip();

  const spaceTitle = getTagValue(spaceEvent.tags, 'title') ?? 'Untitled';
  const spaceDTagValue = getTagValue(spaceEvent.tags, 'd') ?? '';
  const spaceSlug = spaceSlugFromDTag(spaceDTagValue) ?? 'space';
  const pageEntries = parsePageTags(spaceEvent.tags);

  // Build a map of page d-tags to events
  const pageMap = new Map<string, NostrEvent>();
  for (const ev of pageEvents) {
    const dTag = getTagValue(ev.tags, 'd');
    if (dTag) pageMap.set(dTag, ev);
  }

  // Add each page as a markdown file
  for (const entry of pageEntries) {
    const ev = pageMap.get(entry.dTag);
    if (!ev) continue;

    const title = getTagValue(ev.tags, 'title') ?? 'Untitled';
    const content = ev.content ?? '';

    // Use the path to create file structure
    const filePath = entry.path.replace(/^\//, '') + '.md';
    const fullContent = `# ${title}\n\n${content}`;

    zip.file(filePath, fullContent);
  }

  // Add space manifest
  const manifest = {
    title: spaceTitle,
    slug: spaceSlug,
    description: getTagValue(spaceEvent.tags, 'description') ?? '',
    icon: getTagValue(spaceEvent.tags, 'icon') ?? '',
    author: spaceEvent.pubkey,
    pages: pageEntries.map(entry => ({
      dTag: entry.dTag,
      path: entry.path,
      title: pageMap.has(entry.dTag) ? getTagValue(pageMap.get(entry.dTag)!.tags, 'title') ?? '' : '',
      coordinate: `${STELE_PAGE_KIND}:${spaceEvent.pubkey}:${entry.dTag}`,
    })),
    spaceCoordinate: `${STELE_SPACE_KIND}:${spaceEvent.pubkey}:${spaceDTagValue}`,
    exportedAt: new Date().toISOString(),
  };

  zip.file('space.json', JSON.stringify(manifest, null, 2));

  const blob = await zip.generateAsync({ type: 'blob' });
  // Use native download instead of file-saver
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${spaceSlug}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
